import React, { useState } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    IconButton,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormControlLabel,
    Switch,
    Divider,
    Alert
} from '@mui/material';
import { Delete as DeleteIcon, Upload as UploadIcon, Add as AddIcon } from '@mui/icons-material';
import StimuliUploader from '../components/StimuliUploader';
import StimuliSetManager from '../components/StimuliSetManager';

const StimuliBlock = ({ block, index, updateBlock, removeBlock }) => {
    const [showUploader, setShowUploader] = useState(false);
    const [showSetManager, setShowSetManager] = useState(false);

    const handleStimuliSetChange = (stimuliSet) => {
        updateBlock(index, {
            ...block,
            stimuliSet: stimuliSet,
            stimuliSetId: stimuliSet._id
        });
    };

    const handleQuestionChange = (questionIndex, field, value) => {
        const updatedQuestions = [...(block.questions || [])];
        updatedQuestions[questionIndex] = {
            ...updatedQuestions[questionIndex],
            [field]: value
        };
        updateBlock(index, {
            ...block,
            questions: updatedQuestions
        });
    };

    const addQuestion = () => {
        const newQuestion = {
            id: `q_${Date.now()}`,
            text: '',
            type: 'text',
            required: true,
            options: []
        };
        updateBlock(index, {
            ...block,
            questions: [...(block.questions || []), newQuestion]
        });
    };

    const removeQuestion = (questionIndex) => {
        const updatedQuestions = [...(block.questions || [])];
        updatedQuestions.splice(questionIndex, 1);
        updateBlock(index, {
            ...block,
            questions: updatedQuestions
        });
    };

    const handleSettingChange = (setting, value) => {
        updateBlock(index, {
            ...block,
            settings: {
                ...block.settings,
                [setting]: value
            }
        });
    };

    const handleAddOption = (questionIndex) => {
        const updatedQuestions = [...(block.questions || [])];
        const question = updatedQuestions[questionIndex];
        question.options = [...(question.options || []), ''];
        updateBlock(index, {
            ...block,
            questions: updatedQuestions
        });
    };

    const handleOptionChange = (questionIndex, optionIndex, value) => {
        const updatedQuestions = [...(block.questions || [])];
        const question = updatedQuestions[questionIndex];
        question.options[optionIndex] = value;
        updateBlock(index, {
            ...block,
            questions: updatedQuestions
        });
    };

    const removeOption = (questionIndex, optionIndex) => {
        const updatedQuestions = [...(block.questions || [])];
        const question = updatedQuestions[questionIndex];
        question.options.splice(optionIndex, 1);
        updateBlock(index, {
            ...block,
            questions: updatedQuestions
        });
    };

    return (
        <Card sx={{ mb: 2, p: 2 }}>
            <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6">Stimuli Block</Typography>
                    <IconButton onClick={() => removeBlock(index)}>
                        <DeleteIcon />
                    </IconButton>
                </Box>

                {/* Introduction and Outroduction Text */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Block Text
                    </Typography>
                    
                    <TextField
                        fullWidth
                        label="Introduction Text"
                        multiline
                        rows={3}
                        value={block.introText || block.introduction || ''}
                        onChange={(e) => updateBlock(index, { ...block, introText: e.target.value })}
                        placeholder="Text to show before presenting stimuli to participants..."
                        sx={{ mb: 2 }}
                        helperText="This text will be displayed to participants before they see any stimuli"
                    />
                    
                    <TextField
                        fullWidth
                        label="Outroduction Text"
                        multiline
                        rows={3}
                        value={block.outroText || block.outroduction || ''}
                        onChange={(e) => updateBlock(index, { ...block, outroText: e.target.value })}
                        placeholder="Text to show after all stimuli have been presented..."
                        helperText="This text will be displayed to participants after they complete all stimuli"
                    />
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Stimuli Set Selection/Management */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Stimuli Set
                    </Typography>
                    
                    {block.stimuliSet ? (
                        <Box>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                <Typography variant="body2">
                                    <strong>Selected:</strong> {block.stimuliSet.name}
                                </Typography>
                                <Typography variant="caption" display="block">
                                    {block.stimuliSet.files?.length || 0} files
                                </Typography>
                            </Alert>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="outlined"
                                    onClick={() => setShowSetManager(true)}
                                >
                                    Manage Sets
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={() => updateBlock(index, { ...block, stimuliSet: null, stimuliSetId: null })}
                                >
                                    Clear Selection
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Button
                                variant="contained"
                                startIcon={<UploadIcon />}
                                onClick={() => setShowUploader(true)}
                            >
                                Upload New Files
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={() => setShowSetManager(true)}
                            >
                                Choose Existing Set
                            </Button>
                        </Box>
                    )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Presentation Settings */}
                <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                        Presentation Settings
                    </Typography>
                    
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={block.settings?.randomizeOrder || false}
                                    onChange={(e) => handleSettingChange('randomizeOrder', e.target.checked)}
                                />
                            }
                            label="Randomize Order"
                        />
                        
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={block.settings?.showProgress || true}
                                    onChange={(e) => handleSettingChange('showProgress', e.target.checked)}
                                />
                            }
                            label="Show Progress"
                        />
                        
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={block.settings?.allowSkip || false}
                                    onChange={(e) => handleSettingChange('allowSkip', e.target.checked)}
                                />
                            }
                            label="Allow Skip"
                        />
                        
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={block.settings?.autoAdvance || false}
                                    onChange={(e) => handleSettingChange('autoAdvance', e.target.checked)}
                                />
                            }
                            label="Auto Advance"
                        />
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <TextField
                            label="Minimum View Time (seconds)"
                            type="number"
                            value={block.settings?.minViewTime || 0}
                            onChange={(e) => handleSettingChange('minViewTime', parseInt(e.target.value) || 0)}
                            size="small"
                            sx={{ minWidth: 200 }}
                        />
                        
                        {block.settings?.autoAdvance && (
                            <TextField
                                label="Auto Advance Time (seconds)"
                                type="number"
                                value={block.settings?.autoAdvanceTime || 0}
                                onChange={(e) => handleSettingChange('autoAdvanceTime', parseInt(e.target.value) || 0)}
                                size="small"
                                sx={{ minWidth: 200 }}
                            />
                        )}
                    </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Questions */}
                <Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Box>
                            <Typography variant="subtitle1">Questions</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.875rem' }}>
                                These questions will be presented to participants after each stimulus
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={addQuestion}
                            size="small"
                        >
                            Add Question
                        </Button>
                    </Box>

                    {block.questions && block.questions.length > 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                <strong>How questions work:</strong> After viewing each stimulus (image, video, audio, PDF, etc.), 
                                participants will answer these questions before proceeding to the next stimulus. 
                                You can add multiple questions of different types.
                            </Typography>
                        </Alert>
                    )}

                    {(block.questions || []).map((question, questionIndex) => (
                        <Card key={question.id || questionIndex} variant="outlined" sx={{ mb: 2, p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="subtitle2">Question {questionIndex + 1}</Typography>
                                <IconButton 
                                    size="small" 
                                    onClick={() => removeQuestion(questionIndex)}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                                <TextField
                                    label="Question Text"
                                    value={question.text || ''}
                                    onChange={(e) => handleQuestionChange(questionIndex, 'text', e.target.value)}
                                    fullWidth
                                    multiline
                                    rows={2}
                                />
                                
                                <FormControl sx={{ minWidth: 120 }}>
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        value={question.type || 'text'}
                                        label="Type"
                                        onChange={(e) => handleQuestionChange(questionIndex, 'type', e.target.value)}
                                    >
                                        <MenuItem value="text">Text</MenuItem>
                                        <MenuItem value="textarea">Long Text</MenuItem>
                                        <MenuItem value="radio">Single Choice</MenuItem>
                                        <MenuItem value="checkbox">Multiple Choice</MenuItem>
                                        <MenuItem value="likert">Likert Scale</MenuItem>
                                        <MenuItem value="slider">Slider</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            {/* Question Type Help Text */}
                            <Box sx={{ mb: 2, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                <Typography variant="caption" color="textSecondary">
                                    {question.type === 'text' && '📝 Short text input field for brief responses'}
                                    {question.type === 'textarea' && '📄 Large text area for detailed responses'}
                                    {question.type === 'radio' && '⚪ Single selection from multiple options'}
                                    {question.type === 'checkbox' && '☑️ Multiple selections allowed from options'}
                                    {question.type === 'likert' && '📊 Scale rating (e.g., 1-7, Strongly Disagree to Strongly Agree)'}
                                    {question.type === 'slider' && '🎚️ Continuous scale with draggable slider'}
                                </Typography>
                            </Box>

                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={question.required || false}
                                        onChange={(e) => handleQuestionChange(questionIndex, 'required', e.target.checked)}
                                    />
                                }
                                label="Required"
                                sx={{ mb: 2 }}
                            />

                            {/* Options for choice-based questions */}
                            {['radio', 'checkbox', 'likert'].includes(question.type) && (
                                <Box>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Options</Typography>
                                        <Button 
                                            size="small" 
                                            onClick={() => handleAddOption(questionIndex)}
                                        >
                                            Add Option
                                        </Button>
                                    </Box>
                                    {(question.options || []).map((option, optionIndex) => (
                                        <Box key={optionIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                                            <TextField
                                                size="small"
                                                value={option}
                                                onChange={(e) => handleOptionChange(questionIndex, optionIndex, e.target.value)}
                                                placeholder={`Option ${optionIndex + 1}`}
                                                fullWidth
                                            />
                                            <IconButton 
                                                size="small" 
                                                onClick={() => removeOption(questionIndex, optionIndex)}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Card>
                    ))}

                    {(!block.questions || block.questions.length === 0) && (
                        <Alert severity="info" variant="outlined">
                            <Typography variant="body2">
                                <strong>No questions added yet.</strong><br/>
                                Questions are presented to participants after each stimulus. 
                                You can add questions to collect ratings, opinions, or other responses about each stimulus.
                                Click "Add Question" to get started.
                            </Typography>
                        </Alert>
                    )}
                </Box>

                {/* Dialogs */}
                {showUploader && (
                    <StimuliUploader
                        open={showUploader}
                        onClose={() => setShowUploader(false)}
                        onStimuliSetCreated={handleStimuliSetChange}
                    />
                )}

                {showSetManager && (
                    <StimuliSetManager
                        open={showSetManager}
                        onClose={() => setShowSetManager(false)}
                        onStimuliSetSelected={handleStimuliSetChange}
                    />
                )}
            </CardContent>
        </Card>
    );
};

export default StimuliBlock;
